import { useState } from 'react';
import { Badge, Box, Button, HStack, Stack, Text, VStack } from '@chakra-ui/react';
import { HiOutlineShieldCheck, HiOutlineDevicePhoneMobile, HiOutlineEnvelope } from 'react-icons/hi2';
import { useTranslation } from 'react-i18next';
import SectionCard from './SectionCard';
import SetupTwoFactorDialog from './SetupTwoFactorDialog';
import SetupEmailTwoFactorDialog from './SetupEmailTwoFactorDialog';
import DisableTwoFactorDialog from './DisableTwoFactorDialog';

const MethodOption = ({ icon, title, description, actionLabel, actionVariant, onAction }) => (
    <Box flex={1} border="1px solid" borderColor="border.primary" borderRadius={10} p={4}>
        <VStack align="flex-start" gap={2} h="full">
            <HStack gap={2} color="white">
                {icon}
                <Text fontWeight="semibold" fontSize="sm">{title}</Text>
            </HStack>
            <Text color="text.grey" fontSize="xs" flex={1}>{description}</Text>
            <Button variant={actionVariant} size="sm" onClick={onAction} type="button">
                {actionLabel}
            </Button>
        </VStack>
    </Box>
);

const TwoFactorSection = ({ enabled, method, enabledAt, email, onChanged }) => {
    const { t, i18n } = useTranslation('account');
    const [openDialog, setOpenDialog] = useState(null);
    const closeDialog = () => setOpenDialog(null);

    return (
        <SectionCard
            icon={<HiOutlineShieldCheck />}
            title={t('settingsPage.twoFactor.title')}
            subtitle={t('settingsPage.twoFactor.subtitle')}
        >
            {enabled ? (
                <VStack align="flex-start" gap={3}>
                    <HStack gap={2} flexWrap="wrap">
                        <Badge bg="green.900" color="green.300" borderRadius={6} px={2} py={1} fontSize="xs">
                            {t('settingsPage.twoFactor.enabledBadge')}
                        </Badge>
                        <Text color="white" fontSize="xs">
                            {method === 'Email'
                                ? t('settingsPage.twoFactor.activeMethodEmail')
                                : t('settingsPage.twoFactor.activeMethodTotp')}
                        </Text>
                        {enabledAt && (
                            <Text color="text.grey" fontSize="xs">
                                {t('settingsPage.twoFactor.enabledAt', { date: new Date(enabledAt).toLocaleDateString(i18n.language) })}
                            </Text>
                        )}
                    </HStack>
                    <Button variant="outlined" size="sm" onClick={() => setOpenDialog('disable')} type="button">
                        {t('settingsPage.twoFactor.disableAction')}
                    </Button>
                </VStack>
            ) : (
                <VStack align="stretch" gap={3}>
                    <Text color="text.grey" fontSize="sm">{t('settingsPage.twoFactor.description')}</Text>
                    <Text color="white" fontSize="sm">{t('settingsPage.twoFactor.chooseMethod')}</Text>
                    <Stack direction={{ base: 'column', md: 'row' }} gap={3} align="stretch">
                        <MethodOption
                            icon={<HiOutlineDevicePhoneMobile />}
                            title={t('settingsPage.twoFactor.methodTotpTitle')}
                            description={t('settingsPage.twoFactor.methodTotpDescription')}
                            actionLabel={t('settingsPage.twoFactor.enableTotpAction')}
                            actionVariant="primary"
                            onAction={() => setOpenDialog('totp')}
                        />
                        <MethodOption
                            icon={<HiOutlineEnvelope />}
                            title={t('settingsPage.twoFactor.methodEmailTitle')}
                            description={t('settingsPage.twoFactor.methodEmailDescription')}
                            actionLabel={t('settingsPage.twoFactor.enableEmailAction')}
                            actionVariant="outlined"
                            onAction={() => setOpenDialog('email')}
                        />
                    </Stack>
                </VStack>
            )}

            <SetupTwoFactorDialog
                isOpen={openDialog === 'totp'}
                onClose={closeDialog}
                onEnabled={onChanged}
            />
            <SetupEmailTwoFactorDialog
                isOpen={openDialog === 'email'}
                email={email}
                onClose={closeDialog}
                onEnabled={onChanged}
            />
            <DisableTwoFactorDialog
                isOpen={openDialog === 'disable'}
                method={method}
                onClose={closeDialog}
                onDisabled={onChanged}
            />
        </SectionCard>
    );
};

export default TwoFactorSection;
